import { loadConfig, requireApiUrl, saveConfig, type CliConfig } from './config.js'
import { isExpired } from './jwt.js'
import { RefreshTokenError, refreshIdentityToken } from './auth/deviceFlow.js'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
  }
}

export interface ApiClient {
  apiUrl: string
  request<T>(method: string, path: string, options?: RequestOptions): Promise<T>
  requestRaw(method: string, path: string, options?: RequestOptions): Promise<Response>
  /** Mints a scoped access token (optionally workspace-bound / node-bounded) and returns the raw JWT. */
  mintAccessToken(options: MintTokenOptions): Promise<string>
}

export interface MintTokenOptions {
  scopes: string[]
  workspaceId?: string
  accountId?: string
  /** Confine the token to a resource boundary (e.g. a node subtree). */
  resourceId?: string
  resourceKind?: 'Account' | 'Workspace' | 'Node' | 'Publisher'
}

export interface RequestOptions {
  scopes?: string[]
  /** Binds the access token to a workspace (required for calls the backend forwards to Node). */
  tokenWorkspaceId?: string
  body?: unknown
  /** Raw body stream/bytes (overrides json body). */
  rawBody?: BodyInit
  contentType?: string
  query?: Record<string, string | number | boolean | undefined>
  headers?: Record<string, string>
}

const AUDIENCE = 'verentis'

export async function createApiClient(overrides?: Partial<CliConfig>): Promise<ApiClient> {
  const config = { ...(await loadConfig()), ...overrides }
  const apiUrl = requireApiUrl(config)
  const tokenCache = new Map<string, string>()

  // Identity/refresh token state. An env-provided identity token is externally managed — never
  // auto-refreshed or persisted; a device-flow login persists both to ~/.verentis/config.json.
  const envIdentity = process.env.VERENTIS_TOKEN
  let currentIdentity = envIdentity ?? config.identityToken
  let currentRefresh = config.refreshToken
  let refreshInflight: Promise<string> | null = null

  async function persistTokens(idToken: string, refreshToken?: string): Promise<void> {
    // Re-read from disk so we merge (don't clobber fields written elsewhere).
    const disk = await loadConfig()
    disk.identityToken = idToken
    if (refreshToken) disk.refreshToken = refreshToken
    await saveConfig(disk)
  }

  async function clearTokens(): Promise<void> {
    const disk = await loadConfig()
    delete disk.identityToken
    delete disk.refreshToken
    await saveConfig(disk)
  }

  /**
   * Returns a usable identity token, transparently renewing it via the refresh token when it is
   * expired or within 120s of expiry (rotation is persisted). Throws a clear "log in again" error when
   * there is no way to renew. Concurrent callers share a single in-flight refresh.
   */
  async function resolveIdentityToken(): Promise<string> {
    if (!currentIdentity) throw new ApiError(401, 'Not logged in. Run `verentis login`.')
    if (!isExpired(currentIdentity, 120)) return currentIdentity
    if (envIdentity || !currentRefresh) {
      throw new ApiError(401, 'Your session has expired — run `verentis login` again.')
    }
    if (!refreshInflight) {
      refreshInflight = (async () => {
        try {
          const refreshed = await refreshIdentityToken(apiUrl, currentRefresh!)
          currentIdentity = refreshed.idToken
          currentRefresh = refreshed.refreshToken ?? currentRefresh
          await persistTokens(refreshed.idToken, refreshed.refreshToken)
          return refreshed.idToken
        } catch (err) {
          if (err instanceof RefreshTokenError) {
            await clearTokens().catch(() => {})
            throw new ApiError(401, err.message)
          }
          throw err
        } finally {
          refreshInflight = null
        }
      })()
    }
    return refreshInflight
  }

  async function credentialFor(scopes: string[], workspaceId?: string): Promise<string> {
    // API keys are only understood by the Security service, so they are ALWAYS exchanged
    // for a scoped access token — never sent directly to other services.
    const cacheKey = `${workspaceId ?? ''}:${[...scopes].sort().join(' ')}`
    const cached = tokenCache.get(cacheKey)
    if (cached && !isExpired(cached)) return cached
    const token = await mintAccessToken({ scopes, workspaceId })
    tokenCache.set(cacheKey, token)
    return token
  }

  async function mintAccessToken(options: MintTokenOptions): Promise<string> {
    const apiKey = process.env.VERENTIS_API_KEY ?? config.apiKey
    if (!apiKey && !currentIdentity) {
      throw new Error('Not logged in. Run `verentis login` (or pass --api-key / --token).')
    }
    // API keys are exchanged directly; an identity token is renewed via its refresh token when stale.
    const credential = apiKey ?? (await resolveIdentityToken())

    const res = await fetch(`${apiUrl}/v1/tokens/access`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${credential}`,
      },
      body: JSON.stringify({
        audience: AUDIENCE,
        scopes: options.scopes,
        ...(options.workspaceId ? { workspaceId: options.workspaceId } : {}),
        ...(options.accountId ? { accountId: options.accountId } : {}),
        ...(options.resourceId ? { resourceId: options.resourceId, resourceKind: options.resourceKind ?? 'Node' } : {}),
      }),
    })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new ApiError(res.status, `Access-token exchange failed (${res.status}). ${hintFor(res.status)}${text ? `\n${truncate(text)}` : ''}`)
    }
    const data = (await res.json()) as { access_token?: string }
    if (!data.access_token) throw new Error('Access-token response did not include access_token.')
    return data.access_token
  }

  async function requestRaw(method: string, path: string, options: RequestOptions = {}): Promise<Response> {
    const token = await credentialFor(options.scopes ?? [], options.tokenWorkspaceId)
    const url = new URL(`${apiUrl}${path}`)
    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }

    const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
    let body: BodyInit | undefined
    if (options.rawBody !== undefined) {
      body = options.rawBody
      headers['Content-Type'] = options.contentType ?? 'application/octet-stream'
    } else if (options.body !== undefined) {
      body = JSON.stringify(options.body)
      headers['Content-Type'] = 'application/json'
    }

    for (const [key, value] of Object.entries(options.headers ?? {})) headers[key] = value

    const res = await fetch(url, { method, headers, body, ...(options.rawBody !== undefined ? { duplex: 'half' as const } : {}) })
    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new ApiError(res.status, `${method} ${path} failed (${res.status}). ${hintFor(res.status)}${formatErrorBody(text)}`)
    }
    return res
  }

  async function request<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
    const res = await requestRaw(method, path, options)
    if (res.status === 204) return undefined as T
    const text = await res.text()
    if (!text) return undefined as T
    try {
      return JSON.parse(text) as T
    } catch {
      return text as unknown as T
    }
  }

  return { apiUrl, request, requestRaw, mintAccessToken }
}

function hintFor(status: number): string {
  switch (status) {
    case 401:
      return 'Your credential is invalid or expired — run `verentis login` again.'
    case 403:
      return 'You lack the required scope or ownership for this operation.'
    case 404:
      return 'Not found (or not visible to you).'
    default:
      return ''
  }
}

function truncate(text: string, max = 400): string {
  return text.length > max ? `${text.slice(0, max)}…` : text
}

/** Render RFC 7807 ProblemDetails / validation errors compactly; fall back to raw text. */
function formatErrorBody(text: string): string {
  if (!text) return ''
  try {
    const problem = JSON.parse(text) as { title?: string; detail?: string; errors?: Record<string, string[]> }
    const lines: string[] = []
    if (problem.title) lines.push(problem.title)
    if (problem.detail && problem.detail !== problem.title) lines.push(problem.detail)
    for (const [field, messages] of Object.entries(problem.errors ?? {})) {
      for (const message of messages) lines.push(`  ${field}: ${message}`)
    }
    if (lines.length > 0) return `\n${truncate(lines.join('\n'), 800)}`
  } catch {
    // not json — fall through
  }
  return `\n${truncate(text)}`
}

export const SCOPES = {
  publisherRead: 'marketplace.publisher.read',
  publisherCreate: 'marketplace.publisher.create',
  publisherUpdate: 'marketplace.publisher.update',
  packageRead: 'marketplace.package.read',
  packageReadAll: 'marketplace.package.read-all',
  packageCreate: 'marketplace.package.create',
  packagePublish: 'marketplace.package.publish',
  installCreate: 'marketplace.install.create',
  installRead: 'marketplace.install.read',
  installDelete: 'marketplace.install.delete',
} as const
